import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { User, Info } from "react-feather";
import { FORMS, MODULES } from "../../const";
import { vehicleTypeValidation } from "../validations/VehicleTypeCreation";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import InfoList from "../InfoList";
import TooltipIcon from "../ToolTipIcon";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";

const FORM = FORMS.VEHICLE.TYPE.form;

const VehicleTypeAddComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();
    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const initialFormState = {
      vhtId: null,
      vhtType: "",
      vhtDescription: "",
      vhtCategory: "",
      vhtStatus: 2, // Default to Pending/Inactive or 2
      vhtActive: 1, // Default to 1 (Active)
      vhtMaxCbm: "",
      vhtMaxCbf: "",
      vhtCarbonFactor: "",
      vhtLoadingTime: "",
      vhtUnloadingTime: "",
      vhtDemurrage: "",
      createdBy: "",
      createdDate: null,
      lastModifiedBy: "",
      lastModifiedDate: null,
      vhtCompany: company,
    };

    const [vehicleTypeData, setVehicleTypeData] = useState(
      editMode && currentValues
        ? {
            ...initialFormState,
            ...currentValues,
            vhtCompany: company,
          }
        : initialFormState,
    );

    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      setCurrentErrors(errors || {});
    }, [errors]);

    const normalizeCategoryValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      if (typeof value === "number") {
        return value;
      }
      const asNumber = Number(value);
      if (!Number.isNaN(asNumber)) {
        return asNumber;
      }
      const matchingOption = (dropdown.categoryOptions || []).find(
        (option) => option.text === value || option.key === value,
      );
      return matchingOption ? matchingOption.value : value;
    };

    useEffect(() => {
      if (editMode && currentValues) {
        setVehicleTypeData({
          ...initialFormState,
          ...currentValues,
          vhtCategory: normalizeCategoryValue(
            currentValues.vhtCategory ?? currentValues.vetTypeId,
          ),
          vhtCompany: company,
        });
      } else if (!editMode) {
        setVehicleTypeData(initialFormState);
      }
    }, [editMode, currentValues, company, dropdown.categoryOptions]);

    useImperativeHandle(ref, () => ({
      getData: () => vehicleTypeData,
      validateForm: () => {
        console.log("Validating Vehicle Type Data:", vehicleTypeData);
        return vehicleTypeValidation(vehicleTypeData);
      },
      resetForm: () => {
        setVehicleTypeData(initialFormState);
        setCurrentErrors({});
      },
    }));

    const handleInputChange = (field, value) => {
      setVehicleTypeData((prevState) => ({
        ...prevState,
        [field]: value,
      }));
      // Clear the error for this field
      setCurrentErrors((prevState) => {
        const { [field]: _, ...rest } = prevState;
        return rest;
      });
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Vehicle Type Information</h4>

              <div className="two fields">
                <Input
                  required={true}
                  label="Vehicle Type"
                  placeholder="Vehicle Type Name/Code"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_type"
                  editMode={editMode}
                  error={currentErrors.vhtType}
                  resetError={() =>
                    handleInputChange("vhtType", vehicleTypeData.vhtType)
                  }
                  name="vhtType"
                  value={vehicleTypeData.vhtType || ""}
                  onChange={(e) => handleInputChange("vhtType", e.target.value)}
                />

                <Input
                  label="Description"
                  placeholder="Description"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_description"
                  editMode={editMode}
                  error={currentErrors.vhtDescription}
                  resetError={() =>
                    handleInputChange(
                      "vhtDescription",
                      vehicleTypeData.vhtDescription,
                    )
                  }
                  name="vhtDescription"
                  value={vehicleTypeData.vhtDescription || ""}
                  onChange={(e) =>
                    handleInputChange("vhtDescription", e.target.value)
                  }
                />
              </div>

              <div className="three fields">
                {/* Category Dropdown */}
                <Dropdown
                  label="Category"
                  placeholder="Category"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_category"
                  editMode={editMode}
                  error={currentErrors.vhtCategory}
                  resetError={() =>
                    handleInputChange(
                      "vhtCategory",
                      vehicleTypeData.vhtCategory,
                    )
                  }
                  options={dropdown.categoryOptions || []}
                  value={vehicleTypeData.vhtCategory || ""}
                  style={{ height: "20px" }}
                  onChange={(e, data) =>
                    handleInputChange("vhtCategory", data.value)
                  }
                />

                {/* Status Dropdown */}
                <Dropdown
                  required={true}
                  label="Status"
                  placeholder="Status"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_status"
                  editMode={editMode}
                  error={currentErrors.vhtStatus}
                  resetError={() =>
                    handleInputChange("vhtStatus", vehicleTypeData.vhtStatus)
                  }
                  options={dropdown.statusOptions || []}
                  value={
                    vehicleTypeData.vhtStatus !== undefined
                      ? vehicleTypeData.vhtStatus
                      : ""
                  }
                  style={{ height: "20px" }}
                  onChange={(e, data) =>
                    handleInputChange("vhtStatus", data.value)
                  }
                />

                {/* Active Dropdown */}
                <Dropdown
                  required={true}
                  label="Active"
                  placeholder="Active"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_active"
                  editMode={editMode}
                  error={currentErrors.vhtActive}
                  resetError={() =>
                    handleInputChange("vhtActive", vehicleTypeData.vhtActive)
                  }
                  options={dropdown.activeOptions || []}
                  value={
                    vehicleTypeData.vhtActive !== undefined
                      ? vehicleTypeData.vhtActive
                      : ""
                  }
                  style={{ height: "20px" }}
                  onChange={(e, data) =>
                    handleInputChange("vhtActive", data.value)
                  }
                />
              </div>

              <div className="three fields">
                <Input
                  required={true}
                  type="number"
                  step={0.0001}
                  label="Max CBM (Cubic Meters)"
                  placeholder="Max CBM"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_max_cbm"
                  editMode={editMode}
                  error={currentErrors.vhtMaxCbm}
                  resetError={() =>
                    handleInputChange("vhtMaxCbm", vehicleTypeData.vhtMaxCbm)
                  }
                  name="vhtMaxCbm"
                  value={vehicleTypeData.vhtMaxCbm || ""}
                  onChange={(e) =>
                    handleInputChange("vhtMaxCbm", e.target.value)
                  }
                />

                <Input
                  type="number"
                  step={0.0001}
                  label="Max CBF (Cubic Feet)"
                  placeholder="Max CBF"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_max_cbf"
                  editMode={editMode}
                  error={currentErrors.vhtMaxCbf}
                  resetError={() =>
                    handleInputChange("vhtMaxCbf", vehicleTypeData.vhtMaxCbf)
                  }
                  name="vhtMaxCbf"
                  value={vehicleTypeData.vhtMaxCbf || ""}
                  onChange={(e) =>
                    handleInputChange("vhtMaxCbf", e.target.value)
                  }
                />

                <Input
                  required={true}
                  type="number"
                  step={0.0001}
                  label="Carbon Factor (kg CO2e / km)"
                  placeholder="Carbon Factor"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_carbon_factor"
                  editMode={editMode}
                  error={currentErrors.vhtCarbonFactor}
                  resetError={() =>
                    handleInputChange(
                      "vhtCarbonFactor",
                      vehicleTypeData.vhtCarbonFactor,
                    )
                  }
                  name="vhtCarbonFactor"
                  value={vehicleTypeData.vhtCarbonFactor || ""}
                  onChange={(e) =>
                    handleInputChange("vhtCarbonFactor", e.target.value)
                  }
                />
              </div>

              <div className="three fields">
                <Input
                  type="number"
                  step={0.01}
                  label="Loading Time (Hours)"
                  placeholder="Example: 2.5"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_loading_time"
                  editMode={editMode}
                  error={currentErrors.vhtLoadingTime}
                  resetError={() =>
                    handleInputChange(
                      "vhtLoadingTime",
                      vehicleTypeData.vhtLoadingTime,
                    )
                  }
                  name="vhtLoadingTime"
                  value={vehicleTypeData.vhtLoadingTime || ""}
                  onChange={(e) =>
                    handleInputChange("vhtLoadingTime", e.target.value)
                  }
                />

                <Input
                  type="number"
                  step={0.01}
                  label="Unloading Time (Hours)"
                  placeholder="Example: 1.7"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_unloading_time"
                  editMode={editMode}
                  error={currentErrors.vhtUnloadingTime}
                  resetError={() =>
                    handleInputChange(
                      "vhtUnloadingTime",
                      vehicleTypeData.vhtUnloadingTime,
                    )
                  }
                  name="vhtUnloadingTime"
                  value={vehicleTypeData.vhtUnloadingTime || ""}
                  onChange={(e) =>
                    handleInputChange("vhtUnloadingTime", e.target.value)
                  }
                />

                <Input
                  type="number"
                  step={0.01}
                  label={
                    <span
                      style={{ display: "inline-flex", alignItems: "center" }}
                    >
                      Demurrage
                      <TooltipIcon
                        content={
                          <div
                            style={{
                              width: "300px",
                              whiteSpace: "normal",
                              lineHeight: "1.4",
                            }}
                          >
                            The rate charged (typically hourly) when a vehicle
                            of this type exceeds the allowed free time for
                            loading or unloading operations.
                          </div>
                        }
                        position="top center"
                        style={{ marginLeft: "5px" }}
                      >
                        <Info size={14} color="#919191" />
                      </TooltipIcon>
                    </span>
                  }
                  placeholder="Demurrage"
                  module={MODULES.Master}
                  form={FORM}
                  field="vht_demurrage"
                  editMode={editMode}
                  error={currentErrors.vhtDemurrage}
                  resetError={() =>
                    handleInputChange(
                      "vhtDemurrage",
                      vehicleTypeData.vhtDemurrage,
                    )
                  }
                  name="vhtDemurrage"
                  value={vehicleTypeData.vhtDemurrage || ""}
                  onChange={(e) =>
                    handleInputChange("vhtDemurrage", e.target.value)
                  }
                />
              </div>
            </div>

            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: vehicleTypeData.createdBy
                      ? vehicleTypeData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: vehicleTypeData.createdDate
                      ? formatFromJsonDate(vehicleTypeData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: vehicleTypeData.lastModifiedBy
                      ? vehicleTypeData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: vehicleTypeData.lastModifiedDate
                      ? formatFromJsonDate(vehicleTypeData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);

export default VehicleTypeAddComponent;
